/* =====================================================================
 * Sidebar — 사이드 패널 (모드 / 메시지 / 채팅 히스토리)
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styled, { css, keyframes, useTheme } from 'styled-components';
import {
  PanelLeftClose,
  Sun,
  Moon,
  LogOut,
  EllipsisVertical,
  CreditCard,
  Settings2,
  ChevronUp,
  ChevronDown,
  Star,
  MessageCircleQuestion,
  Shield,
  Box,
  Calculator,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useChatStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { useTeamStore } from '@auw/stores';
import { useUIStore } from '@auw/stores';
import type { WorkspaceMode } from '@auw/workspace-utils';
import { planLabel } from '@auw/workspace-utils';
import { useFeatureFlags } from '@auw/feature-flags';
import {
  ADMIN_SKILLS_MANAGE,
  ADMIN_USERS_VIEW,
  usePermission,
} from '@auw/permissions';
import {
  resolveAppNameLogo,
  resolveSpecializedAgents,
  resolveTenantModeOrder,
  resolveWorkspaceModeAvatar,
  useTenant,
} from '@auw/tenant';
import type { TenantSpecializedAgent } from '@auw/tenant';
import { WORKSPACE_MODES, WORKSPACE_ORDER, isWorkspaceMode } from '@auw/workspace-utils';
import { workspaceModeToAgentProfileId } from '@auw/i18n';
import {
  isCoreWorkspaceMode,
  readSidebarFavoriteModes,
  writeSidebarFavoriteModes,
} from '@auw/workspace-utils';
import { avatarColor, avatarInitial } from '@auw/workspace-utils';
import { modeToPath } from '@auw/workspace-utils';
import { Button } from '@auw/workspace-ui';
import { DropdownMenu } from '@auw/workspace-ui';
import { logoutCurrentSession } from '@auw/api-client';
import { hasAuthSessionContext, logoutActiveAccount } from '@auw/api-client';
import { getActiveAccountId } from '@auw/api-client';
import { getWorkspaceStats } from '@auw/api-client';
import { useDMStore } from '@auw/workspace-ui';
import { useDMConversationStore } from '@auw/stores';
import { useDMUnreadPolling } from './hooks/useDMUnreadPolling';
import { TeamSelectorModal } from './TeamSelectorModal';
import { SidebarUsageSection } from './SidebarUsageSection';
import { useWorkspaceNavigationStore } from '@auw/stores';
import { useWorkspaceNavigate } from '@auw/workspace-features';
import { emitAppEvent } from '@auw/stores';
import { clearQuerySessionStorage } from '@auw/workspace-utils';
import { resetRuntimeSessionState } from '@auw/stores';

interface SidebarNavItemConfig {
  mode: WorkspaceMode;
  label: string;
  unreadCount?: number;
}

/* ------------------------------------------------------------------ */
/* 전문 에이전트 — iconKey ↔ lucide 아이콘 매핑                           */
/*                                                                     */
/* 매니페스트는 JSON-직렬화 가능해야 하므로 함수 레퍼런스 대신 문자열       */
/* `iconKey`를 받는다. 여기서 문자열 → 컴포넌트로 해석한다. 알 수 없는      */
/* 키는 기본값(`Sparkles`)으로 fallback 해서 새 키 추가가 매니페스트       */
/* schema 를 깨뜨리지 않게 한다.                                          */
/* ------------------------------------------------------------------ */
const SPECIALIZED_AGENT_ICONS: Record<string, LucideIcon> = {
  cad: Box,
  box: Box,
  estimate: Calculator,
  calculator: Calculator,
  sparkles: Sparkles,
};

function resolveSpecializedAgentIcon(iconKey?: string): LucideIcon {
  if (!iconKey) return Sparkles;
  return SPECIALIZED_AGENT_ICONS[iconKey] ?? Sparkles;
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Overlay = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: ${({ $open }) => ($open ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: ${({ theme }) => theme.colors.bgOverlay};
    z-index: 40;
    animation: fadeIn 0.2s ease;
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  }
`;

const sidebarIntroIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-18px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Container = styled.aside<{ $open: boolean }>`
  width: ${({ $open, theme }) => ($open ? theme.sizes.sidebarWidth : '0')};
  height: 100%;
  overflow: hidden;
  flex-shrink: 0;
  z-index: 50;
  transition: width ${({ theme }) => theme.transitions.normal};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.sizes.sidebarWidth};
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    transition: transform ${({ theme }) => theme.transitions.normal};
    transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
  }
`;

const InnerWrap = styled.div<{ $open: boolean; $introActive: boolean }>`
  min-width: ${({ theme }) => theme.sizes.sidebarWidth};
  width: ${({ theme }) => theme.sizes.sidebarWidth};
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(23, 23, 23, 0.82)'
      : 'rgba(249, 249, 249, 0.78)'};
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border-right: 1px solid
    ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)'};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity ${({ theme }) => theme.transitions.fast};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transform: translateX(0);
  ${({ $open, $introActive }) =>
    $open && $introActive && css`
      animation: ${sidebarIntroIn} 420ms 40ms cubic-bezier(0.22, 1, 0.36, 1) both;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    opacity: 1;
    pointer-events: auto;
  }
`;

/* ---- Header: 로고 좌측 + 패널 접기 버튼 우측 ---- */

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  padding: ${({ theme }) => theme.sidebar.headerPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: ${({ theme }) => theme.sidebarMobile.headerHeight};
    padding: ${({ theme }) => theme.sidebarMobile.headerPad};
  }
`;

const AppNameLogo = styled.img`
  height: ${({ theme }) => theme.sidebar.logoHeight};
  object-fit: contain;
  user-select: none;
  cursor: pointer;
  margin-left: 4px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: ${({ theme }) => theme.sidebarMobile.logoHeight};
  }
`;

/**
 * Square brand mark fallback rendered when the tenant manifest does
 * NOT supply a wordmark (`assets.appNameLogo`). Wordmarks are
 * landscape-oriented and only constrain `height`; brand marks are
 * 1:1 (favicon-shaped SVG/PNG), so we lock width/height together to
 * avoid a stretched render when an SVG ships an arbitrary intrinsic
 * size. The size matches the wordmark's height on each breakpoint so
 * the header row keeps the same vertical rhythm regardless of which
 * slot supplied the image.
 */
const BrandMarkLogo = styled.img`
  width: ${({ theme }) => theme.sidebar.logoHeight};
  height: ${({ theme }) => theme.sidebar.logoHeight};
  object-fit: contain;
  user-select: none;
  cursor: pointer;
  margin-left: 4px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.sidebarMobile.logoHeight};
    height: ${({ theme }) => theme.sidebarMobile.logoHeight};
  }
`;

/* ---- 섹션 (접기/펼치기) ---- */

const SectionTitleRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.sidebar.sectionFontSize};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: ${({ theme }) => theme.sidebar.sectionPad};
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.sidebarMobile.sectionFontSize};
    padding: ${({ theme }) => theme.sidebarMobile.sectionPad};
  }
`;

const SectionChevron = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: transform 0.15s;
`;

const SectionBody = styled.div<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? '2000px' : '0')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: max-height 0.25s ease, opacity 0.2s ease;
`;

const SectionList = styled.div`
  padding: ${({ theme }) => theme.sidebar.sectionListPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.sidebarMobile.sectionListPad};
  }
`;

const ModeItem = styled.div<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sidebar.itemGap};
  padding: ${({ theme }) => theme.sidebar.itemPad};
  border: none;
  background: transparent;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.sidebar.itemFontSize};
  font-weight: 400;
  cursor: pointer;
  transition: background 0.15s ease-out, box-shadow 0.15s ease-out;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      font-weight: 600;
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff'};
      box-shadow: ${theme.mode === 'dark'
        ? '0 1px 4px rgba(0,0,0,0.3), inset 0 0.5px 0 rgba(255,255,255,0.04)'
        : '0 1px 4px rgba(0,0,0,0.05), 0 0 0 0.5px rgba(0,0,0,0.03)'};
    `}

  > svg {
    flex-shrink: 0;
    color: ${({ $active, theme }) =>
      $active ? theme.colors.textPrimary : theme.colors.textMuted};
    transition: color 0.15s ease-out;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.sidebarMobile.itemGap};
    padding: ${({ theme }) => theme.sidebarMobile.itemPad};
    font-size: ${({ theme }) => theme.sidebarMobile.itemFontSize};
  }
`;

const ModeItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
`;

/** DM 안읽은 메시지 뱃지 */
const SidebarUnreadBadge = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.error};
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;
  line-height: 1;
`;

const FavoriteBtn = styled.button<{ $favorited: boolean; $alwaysVisible?: boolean }>`
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${({ $favorited, theme }) => ($favorited ? '#f5b301' : theme.colors.textMuted)};
  cursor: pointer;
  visibility: ${({ $favorited, $alwaysVisible }) => ($favorited || $alwaysVisible ? 'visible' : 'hidden')};
  opacity: ${({ $favorited, $alwaysVisible }) => ($favorited || $alwaysVisible ? 1 : 0)};
  transition:
    opacity ${({ theme }) => theme.transitions.fast},
    visibility ${({ theme }) => theme.transitions.fast},
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast};

  ${ModeItem}:hover & {
    visibility: visible;
    opacity: 1;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
    color: ${({ $favorited }) => ($favorited ? '#f5b301' : '#d4a017')};
    transform: scale(1.03);
  }
`;

/** ModeItem 내 텍스트 라벨 — 12글자 초과 시 ... 처리 */
const ModeItemLabel = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 18ch;
`;

/** 워크스페이스 모드 아바타 (모노톤 사각형) */
const ModeAvatarWrap = styled.span`
  position: relative;
  width: 28px;
  height: 28px;
  min-width: 28px;
  flex-shrink: 0;
`;

const ModeAvatarCircle = styled.span<{ $active?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active, theme }) =>
    $active
      ? (
        theme.mode === 'dark'
          ? 'linear-gradient(145deg, #4b4b4f, #3a3a3d)'
          : '#ffffff'
      )
      : (
        theme.mode === 'dark'
          ? 'linear-gradient(145deg, #3d3d40, #313134)'
          : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'
      )};
  color: ${({ $active, theme }) => (
    $active
      ? (theme.mode === 'dark' ? '#ececef' : '#1f2937')
      : (theme.mode === 'dark' ? '#d2d2d7' : '#2c2c2e')
  )};
  font-weight: 700;
  font-size: 11px;
  line-height: 1;
  user-select: none;
  overflow: hidden;
  box-shadow: ${({ $active, theme }) =>
    $active
      ? (
        theme.mode === 'dark'
          ? '0 1px 5px rgba(0,0,0,0.28), inset 0 0 0 1px rgba(255,255,255,0.06)'
          : '0 1px 6px rgba(0,0,0,0.12), inset 0 0 0 1px rgba(255,255,255,0.35)'
      )
      : (
        theme.mode === 'dark'
          ? '0 1px 4px rgba(0,0,0,0.32), inset 0 0.5px 0 rgba(255,255,255,0.04)'
          : '0 1px 4px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(0,0,0,0.04)'
      )};
  position: relative;

  svg {
    color: inherit;
    flex-shrink: 0;
  }

`;

const ModeAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
`;

const ModeAvatarDot = styled.span<{ $color: string }>`
  display: none;
`;

/* ---- 하단 고정 영역 (사용량 + 프로필) ---- */

const BottomFixed = styled.div`
  margin-top: auto;
  flex-shrink: 0;
`;

const ContactBtn = styled.button`
  width: calc(100% - 16px);
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 8px 2px;
  padding: 9px 14px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: transparent;
  transition: background 0.15s ease-out, color 0.15s ease-out;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.textMuted};
    transition: color 0.15s ease-out;
  }

  &:hover svg {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const TopScrollable = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/* ---- 상단 개인/팀 토글 ---- */

const WsToggleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin: 0 12px 6px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 10px;
  padding: 3px;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'inset 0 0.5px 1px rgba(0,0,0,0.2)'
      : 'inset 0 0.5px 1px rgba(0,0,0,0.04)'};
`;

const WsToggleBtn = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 6px 0;
  border: none;
  border-radius: 7px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-out;

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.9)'};
          color: ${theme.colors.textPrimary};
          box-shadow: ${theme.mode === 'dark'
            ? '0 1px 3px rgba(0,0,0,0.3), inset 0 0.5px 0 rgba(255,255,255,0.04)'
            : '0 1px 3px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.02)'};
        `
      : css`
          background: transparent;
          color: ${theme.colors.textMuted};
          box-shadow: none;
        `}

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ---- Plan chip ---- */

const PlanChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.5rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  line-height: 1.5;
`;

/* ---- Profile name row (with chip) ---- */

const ProfileNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

/* ---- Footer ---- */

const Footer = styled.div`
  padding: 4px 8px 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 4px 6px 10px;
  }
`;

const ProfileRow = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: 10px;
  padding: ${({ theme }) => theme.sidebar.profilePad};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sidebar.profileGap};
  cursor: default;
  transition: background 0.15s ease-out, box-shadow 0.15s ease-out;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 3px rgba(0,0,0,0.25), inset 0 0.5px 0 rgba(255,255,255,0.03)'
      : '0 1px 3px rgba(0,0,0,0.04), 0 0 0 0.5px rgba(0,0,0,0.02)'};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.sidebarMobile.profilePad};
    gap: ${({ theme }) => theme.sidebarMobile.profileGap};
  }
`;

const AvatarCircle = styled.div<{ $bg: string; $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  border-radius: 22%;
  background: ${({ $bg, theme }) =>
    theme.mode === 'dark'
      ? `linear-gradient(rgba(0,0,0,0.16), rgba(0,0,0,0.16)), ${$bg}`
      : $bg};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'transparent'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.28)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: ${({ $size }) => Math.round($size * 0.42)}px;
  line-height: 1;
  user-select: none;
  flex-shrink: 0;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  filter: ${({ theme }) =>
    theme.mode === 'dark' ? 'brightness(0.9) saturate(0.9)' : 'none'};
`;

const ProfileTextWrap = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const ProfileName = styled.span`
  font-size: ${({ theme }) => theme.sidebar.profileNameFontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.sidebarMobile.profileNameFontSize};
  }
`;

const ProfileEmail = styled.span`
  font-size: ${({ theme }) => theme.sidebar.profileEmailFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.sidebarMobile.profileEmailFontSize};
  }
`;

const MoreBtn = styled.button`
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const Sidebar: React.FC = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  /* Phase 11-5: sidebar sub-feature gates.  Each flag toggles a
   * specific sidebar surface independently of the underlying mode
   * permissions, so an enterprise tenant can hide e.g. the team
   * switcher pill without disabling the team APIs themselves. */
  const sidebarFavoritesEnabled = isFeatureEnabled('sidebar.favorites');
  const sidebarTeamSwitcherEnabled = isFeatureEnabled('sidebar.teamSwitcher');
  const sidebarManagementSectionEnabled = isFeatureEnabled('sidebar.managementSection');
  const sidebarUsageUpgradeEnabled = isFeatureEnabled('sidebar.usageUpgrade');
  const sidebarProfileMenuEnabled = isFeatureEnabled('sidebar.profileMenu');
  /* P11-5j: enterprise-only entry that deep-links into the
   * `apps/enterprise` admin console (`/admin/...`).  B2C disables the
   * flag in `b2c.manifest.ts` so the entry never reaches the DOM, even
   * though the user might still hold the underlying admin permissions. */
  const sidebarAdminConsoleLinkEnabled = isFeatureEnabled('sidebar.adminConsoleLink');
  /* Per-surface visibility for the bottom-fixed area.
   *
   *   - `sidebar.contactLink`     — the "문의하기" button below the
   *     workspace nav. Hides only the entry; the contact tab inside
   *     the settings modal is still controlled by
   *     `page.settings.contact`.
   *
   *   - `sidebar.changePlanItem`  — the "변경하기" item inside the
   *     bottom profile dropdown. AND-combined with `page.pricing`
   *     (mirrors the storage-sidebar pattern in `StorageSidebar.tsx`):
   *     a tenant that turns off the SaaS pricing page in its manifest
   *     automatically loses this entry too, so the menu can never
   *     dead-link into a hidden route. The dedicated
   *     `sidebar.changePlanItem` flag is kept for the inverse case —
   *     leaving `page.pricing` on but suppressing the dropdown shortcut
   *     for UX reasons (e.g. funneling all plan changes through a
   *     dedicated banner).
   */
  const sidebarContactLinkEnabled = isFeatureEnabled('sidebar.contactLink');
  const pricingPageEnabled = isFeatureEnabled('page.pricing');
  const sidebarChangePlanItemEnabled =
    isFeatureEnabled('sidebar.changePlanItem') && pricingPageEnabled;
  const tenant = useTenant();
  /**
   * Wordmark resolved against the active theme. `null` means "render
   * no wordmark" — this is the path on-prem tenants that ship a
   * theme-neutral SVG `agentLogo` take, and it also matches the legacy
   * B2C dark-mode behaviour (the previous `theme.mode !== 'dark'`
   * gate hid the light wordmark to protect contrast). When B2C ships
   * a dedicated dark variant via `assets.appNameLogoDark`, the dark
   * branch swaps in that file instead of returning null.
   */
  const appNameLogo = resolveAppNameLogo(
    tenant,
    theme.mode === 'dark' ? 'dark' : 'light',
  );
  /**
   * Effective ordering for the "Workspace" section. Mirrors the
   * app-shell default (`WORKSPACE_ORDER`) unless the active tenant
   * manifest overrides it via `workspace.modeOrder` — B2C ships the
   * exact same list for zero regression (Phase 4c).
   */
  const effectiveWorkspaceOrder = useMemo(
    () => resolveTenantModeOrder(tenant, WORKSPACE_ORDER, isWorkspaceMode),
    [tenant],
  );
  /**
   * "전문 에이전트" 섹션에 노출할 항목. `enabled === false` 인 엔트리는
   * resolveSpecializedAgents 에서 이미 제외된 상태로 도착한다. 빈
   * 배열이면 섹션 전체가 노출되지 않는다 (테넌트가 specializedAgents
   * 필드를 비우면 자연스럽게 미노출).
   */
  const specializedAgents = useMemo(
    () => resolveSpecializedAgents(tenant),
    [tenant],
  );
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const conversations = useChatStore((s) => s.conversations);
  const conversationOrder = useChatStore((s) => s.conversationOrder);
  const activeId = useChatStore((s) => s.activeId);
  const toggleDarkMode = useChatStore((s) => s.toggleDarkMode);
  const darkMode = useChatStore((s) => s.darkMode);
  const activeMode = useChatStore((s) => s.activeMode);
  const addToast = useChatStore((s) => s.addToast);
  /* ---- userStore 구독 ---- */
  const user = useUserStore((s) => s.user);
  const usage = useUserStore((s) => s.usage);
  const updateSidebarFavoriteModes = useUserStore((s) => s.updateSidebarFavoriteModes);
  const onboardingPending = user?.onboardingCompleted === false;
  const onboardingUserId = user?.userId ?? null;
  // Role-gated manage items are resolved via `@auw/permissions` so
  // tenants can rebrand the `owner` role (or add new ones) without
  // touching this component. B2C keeps parity: only `owner` grants
  // both permissions today — see `b2c.manifest.ts#roles`.
  const canManageUsers = usePermission(ADMIN_USERS_VIEW);
  const canManageSkills = usePermission(ADMIN_SKILLS_MANAGE);
  const canManageTeamWorkspace = !!user;
  const [nonCriticalReady, setNonCriticalReady] = useState(false);
  const [playOnboardingIntro, setPlayOnboardingIntro] = useState(false);

  useEffect(() => {
    if (!user) {
      setNonCriticalReady(false);
      return;
    }

    let cancelled = false;
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(() => {
        if (!cancelled) setNonCriticalReady(true);
      }, { timeout: 1_500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timer = window.setTimeout(() => {
      if (!cancelled) setNonCriticalReady(true);
    }, 1_000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [user]);

  useEffect(() => {
    if (!onboardingPending || !onboardingUserId || !sidebarOpen) {
      setPlayOnboardingIntro(false);
      return;
    }
    setPlayOnboardingIntro(true);
    const timer = window.setTimeout(() => {
      setPlayOnboardingIntro(false);
    }, 520);
    return () => window.clearTimeout(timer);
  }, [onboardingPending, onboardingUserId, sidebarOpen]);

  /* ---- teamStore 구독 ---- */
  const myTeams = useTeamStore((s) => s.teams);
  const teamsLoaded = useTeamStore((s) => s.isLoaded);
  const loadMyTeams = useTeamStore((s) => s.loadMyTeams);
  const wsView = useTeamStore((s) => s.workspaceView);
  const setWsView = useTeamStore((s) => s.setWorkspaceView);

  useEffect(() => {
    if (!teamsLoaded && user) {
      loadMyTeams();
    }
  }, [teamsLoaded, user, loadMyTeams]);

  useEffect(() => {
    if (wsView === 'personal') return;
    if (myTeams.some((t) => t.team_id === wsView)) return;
    setWsView(myTeams.length > 0 ? myTeams[0].team_id : 'personal');
  }, [wsView, myTeams, setWsView]);

  /* ---- 파일 저장소 사용량 ---- */
  const isLoggedIn = !!user;
  const [fileStorageBytes, setFileStorageBytes] = useState(0);
  const hasSessionContext = isLoggedIn && hasAuthSessionContext();
  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let queued = false;

    const fetchStorage = async () => {
      if (!user || !nonCriticalReady || !hasSessionContext) return;
      if (inFlight) {
        queued = true;
        return;
      }
      inFlight = true;
      try {
        const stats = await getWorkspaceStats();
        if (!disposed) {
          setFileStorageBytes(stats.total_file_size_bytes);
        }
      } catch { /* */ }
      finally {
        inFlight = false;
        if (queued && !disposed) {
          queued = false;
          void fetchStorage();
        }
      }
    };

    const scheduleFetchStorage = () => {
      queued = true;
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (!queued) return;
        queued = false;
        void fetchStorage();
      }, 250);
    };

    if (user && nonCriticalReady && hasSessionContext) {
      void fetchStorage();
    }
    const onItemsChanged = () => {
      if (!nonCriticalReady || !hasSessionContext) return;
      scheduleFetchStorage();
    };
    window.addEventListener('workspace:itemsChanged', onItemsChanged);
    return () => {
      disposed = true;
      if (timer) {
        clearTimeout(timer);
      }
      window.removeEventListener('workspace:itemsChanged', onItemsChanged);
    };
  }, [hasSessionContext, user, nonCriticalReady]);

  /* ---- DM unread count 구독 ---- */
  const dmUnreadCounts = useDMStore((s) => s.unreadCounts);
  const dmWsConnected = useDMStore((s) => s.wsConnected);
  const dmConversations = useDMConversationStore((s) => s.conversations);

  const totalDMUnread = useMemo(() => {
    const agentTotal = Object.values(dmUnreadCounts).reduce((a, b) => a + b, 0);
    const convTotal = dmConversations.reduce((a, c) => a + (c.unread_count ?? 0), 0);
    return agentTotal + convTotal;
  }, [dmUnreadCounts, dmConversations]);

  /* ---- 앱 레벨 DM unread count — 스마트 폴링 (비활성 탭에서 불필요한 요청 방지) ---- */
  const messageModeEnabled = isFeatureEnabled('mode.message');
  useDMUnreadPolling({
    messageModeEnabled,
    hasSessionContext,
    dmWsConnected,
    nonCriticalReady,
  });

  const [imgError, setImgError] = useState(false);
  const [modeAvatarErrorIds, setModeAvatarErrorIds] = useState<Record<string, boolean>>({});
  const favoriteStorageScope = useMemo(
    () => getActiveAccountId() ?? user?.userId ?? 'anonymous',
    [user?.userId],
  );
  const [favoriteModes, setFavoriteModes] = useState<WorkspaceMode[]>(() => readSidebarFavoriteModes('anonymous'));
  const favoriteModesRef = useRef<WorkspaceMode[]>(readSidebarFavoriteModes('anonymous'));
  const migratedFavoriteScopesRef = useRef<Set<string>>(new Set());
  const agentPictures: Record<string, string | null> = {};
  const [menu, setMenu] = useState<{ open: boolean; x: number; y: number }>({
    open: false,
    x: 0,
    y: 0,
  });
  /* ---- 섹션 접기/펼치기 상태 (default: 펼침) ---- */
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({
    favorites: true,
    workspace: true,
    specializedAgents: true,
    teamWorkspace: true,
    message: true,
    manage: true,
  });
  const toggleSection = (key: string) =>
    setSectionOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ---- 워크스페이스 뷰 전환 (개인 vs 팀) ---- */
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const selectedTeam = wsView !== 'personal'
    ? myTeams.find((t) => t.team_id === wsView)
    : null;
  const isTeamView = !!selectedTeam;
  const rememberedIdByMode = useWorkspaceNavigationStore((s) => s.rememberedIdByMode);
  const rememberModeSelection = useWorkspaceNavigationStore((s) => s.rememberModeSelection);
  const {
    openWorkspaceMode,
    openWorkspaceDraft,
    openWorkspaceItem,
  } = useWorkspaceNavigate();

  /** 팀 토글 클릭 핸들러 */
  const handleTeamToggle = () => {
    if (myTeams.length === 0) return;
    if (myTeams.length === 1) {
      // 1개면 바로 전환
      setWsView(myTeams[0].team_id);
    } else {
      // 2개 이상이면 모달
      setTeamModalOpen(true);
    }
  };

  const START_PAGE_MODES: WorkspaceMode[] = ['agent', 'document'];

  useEffect(() => {
    if (!activeId) return;
    const conv = conversations[activeId];
    if (!conv) return;
    if (conv.mode !== activeMode) return;
    rememberModeSelection(conv.mode, activeId);
  }, [activeId, activeMode, conversations, rememberModeSelection]);

  const handleModeSwitch = (mode: WorkspaceMode) => {
    const rememberedId = rememberedIdByMode[mode];
    if (rememberedId) {
      const rememberedConv = conversations[rememberedId];
      if (rememberedConv && rememberedConv.mode === mode) {
        openWorkspaceItem(rememberedId);
        return;
      }
    }

    const existingNonShared = conversationOrder.find((id) => {
      const c = conversations[id];
      return c && c.mode === mode && !c.sharedGroup;
    });
    const existing = existingNonShared ?? conversationOrder.find((id) => {
      const c = conversations[id];
      return c && c.mode === mode;
    });

    if (existing) {
      openWorkspaceItem(existing);
    } else if (!START_PAGE_MODES.includes(mode)) {
      openWorkspaceMode(mode);
    } else {
      openWorkspaceDraft(mode);
    }
  };

  const openModePage = useCallback((mode: WorkspaceMode) => {
    if (!isFeatureEnabled(`mode.${mode}` as const)) {
      addToast({
        type: 'warning',
        title: t('sidebar.inDevelopment'),
      });
      return;
    }
    /*
     * doc-agent 는 "문서 목록" 랜딩페이지를 진입점으로 가져야 한다.
     * openWorkspaceMode 는 rememberedId / 마지막 conversation 이 있으면 그 항목으로
     * 리다이렉트하므로 여기서는 openWorkspaceDraft 로 명시적으로 랜딩만 연다.
     */
    if (mode === 'doc-agent') {
      openWorkspaceDraft('doc-agent');
      return;
    }
    if (mode === 'widget' || mode === 'feed' || mode === 'tools' || mode === 'plugin' || mode === 'app-manage' || mode === 'admin-page') {
      openWorkspaceMode(mode);
      return;
    }
    handleModeSwitch(mode);
  }, [addToast, handleModeSwitch, isFeatureEnabled, openWorkspaceDraft, openWorkspaceMode, t]);

  const workspaceItems = useMemo<SidebarNavItemConfig[]>(
    () => effectiveWorkspaceOrder
      .filter((mode) => isFeatureEnabled(`mode.${mode}` as const))
      .map((mode) => ({
        mode,
        label: t(WORKSPACE_MODES[mode].labelKey),
      })),
    [effectiveWorkspaceOrder, isFeatureEnabled, t],
  );

  // Tenant-level mode gates apply to every sidebar section, not just
  // the main workspace order. Without these checks a tenant that
  // disables `mode.message` / `mode.widget` / `mode.admin-page` /
  // ... in its manifest would still see those entries because the
  // section builders below render unconditionally. Centralising the
  // gate here keeps WorkspaceRouter's deep-link guard and the
  // sidebar in lockstep — the sidebar no longer hands the user a
  // link that lands on the placeholder page.
  const teamWorkspaceItems = useMemo<SidebarNavItemConfig[]>(
    () =>
      canManageTeamWorkspace && isFeatureEnabled('mode.admin-page')
        ? [{ mode: 'admin-page', label: t('nav.teamManage') }]
        : [],
    [canManageTeamWorkspace, isFeatureEnabled, t],
  );

  const personalItems = useMemo<SidebarNavItemConfig[]>(() => {
    const items: SidebarNavItemConfig[] = [];
    if (isFeatureEnabled('mode.widget')) {
      items.push({ mode: 'widget', label: t(WORKSPACE_MODES.widget.labelKey) });
    }
    if (isFeatureEnabled('mode.feed')) {
      items.push({ mode: 'feed', label: t(WORKSPACE_MODES.feed.labelKey) });
    }
    if (isFeatureEnabled('mode.tools')) {
      items.push({ mode: 'tools', label: t(WORKSPACE_MODES.tools.labelKey) });
    }
    if (isFeatureEnabled('mode.message')) {
      items.push({
        mode: 'message',
        label: t(WORKSPACE_MODES.message.labelKey),
        unreadCount: totalDMUnread,
      });
    }
    return items;
  }, [isFeatureEnabled, t, totalDMUnread]);

  const manageItems = useMemo<SidebarNavItemConfig[]>(() => {
    const items: SidebarNavItemConfig[] = [];
    if (isFeatureEnabled('mode.storage')) {
      items.push({ mode: 'storage', label: t(WORKSPACE_MODES.storage.labelKey) });
    }
    if (isFeatureEnabled('mode.plugin')) {
      items.push({ mode: 'plugin', label: t(WORKSPACE_MODES.plugin.labelKey) });
    }
    if (canManageUsers && isFeatureEnabled('mode.app-manage')) {
      items.push({ mode: 'app-manage' as const, label: t('nav.appManage') });
    }
    if (canManageSkills && isFeatureEnabled('mode.skill-create')) {
      items.push({ mode: 'skill-create' as const, label: t('nav.skillCreate') });
    }
    return items;
  }, [t, canManageUsers, canManageSkills, isFeatureEnabled]);

  const accessibleItemsByMode = useMemo(() => {
    const map = new Map<WorkspaceMode, SidebarNavItemConfig>();
    [...workspaceItems, ...teamWorkspaceItems, ...personalItems, ...manageItems].forEach((item) => {
      map.set(item.mode, item);
    });
    return map;
  }, [workspaceItems, teamWorkspaceItems, personalItems, manageItems]);

  const favoriteItems = useMemo(
    () =>
      favoriteModes
        .map((mode) => accessibleItemsByMode.get(mode))
        .filter((item): item is SidebarNavItemConfig => Boolean(item)),
    [accessibleItemsByMode, favoriteModes],
  );

  /* ---- me 호환 객체 (userStore 기반) ---- */
  const me = user
    ? { name: user.name, email: user.email, picture: user.picture }
    : null;
  const isGuestPreview = !me?.email && !me?.name;
  const guestAvatarBg = theme.mode === 'dark' ? '#6b7280' : '#9ca3af';
  const profileAvatarBg = isGuestPreview
    ? guestAvatarBg
    : avatarColor(me?.email || me?.name || 'U', theme.mode === 'dark');
  const profileAvatarText = isGuestPreview ? 'U' : avatarInitial(me?.name, me?.email);

  useEffect(() => {
    setImgError(false);
  }, [me?.picture]);

  const markModeAvatarError = (avatarKey: string) => {
    setModeAvatarErrorIds((prev) => {
      if (prev[avatarKey]) return prev;
      return { ...prev, [avatarKey]: true };
    });
  };

  useEffect(() => {
    favoriteModesRef.current = favoriteModes;
  }, [favoriteModes]);

  const favoriteModeSet = useMemo(() => new Set<WorkspaceMode>(favoriteModes), [favoriteModes]);

  const visibleWorkspaceItems = useMemo(
    () => workspaceItems.filter((item) => !favoriteModeSet.has(item.mode)),
    [favoriteModeSet, workspaceItems],
  );

  const visibleTeamWorkspaceItems = useMemo(
    () => teamWorkspaceItems.filter((item) => !favoriteModeSet.has(item.mode)),
    [favoriteModeSet, teamWorkspaceItems],
  );

  const visiblePersonalItems = useMemo(
    () => personalItems.filter((item) => !favoriteModeSet.has(item.mode)),
    [favoriteModeSet, personalItems],
  );

  const visibleManageItems = useMemo(
    () => manageItems.filter((item) => !favoriteModeSet.has(item.mode)),
    [favoriteModeSet, manageItems],
  );

  useEffect(() => {
    const cachedModes = readSidebarFavoriteModes(favoriteStorageScope);

    if (!user?.userId) {
      favoriteModesRef.current = cachedModes;
      setFavoriteModes(cachedModes);
      return;
    }

    const serverModes = Array.isArray(user.sidebarFavoriteModes) ? user.sidebarFavoriteModes : [];
    if (serverModes.length > 0 || cachedModes.length === 0) {
      favoriteModesRef.current = serverModes;
      setFavoriteModes(serverModes);
      writeSidebarFavoriteModes(favoriteStorageScope, serverModes);
      return;
    }

    // 기존 localStorage 즐겨찾기를 최초 한 번 서버 프로필로 승격한다.
    favoriteModesRef.current = cachedModes;
    setFavoriteModes(cachedModes);
    writeSidebarFavoriteModes(favoriteStorageScope, cachedModes);

    if (migratedFavoriteScopesRef.current.has(favoriteStorageScope)) return;
    migratedFavoriteScopesRef.current.add(favoriteStorageScope);

    void updateSidebarFavoriteModes(cachedModes).catch(() => {
      // 실패 시 현재 브라우저 캐시는 유지하고, 반복 재시도는 막는다.
    });
  }, [favoriteStorageScope, updateSidebarFavoriteModes, user?.sidebarFavoriteModes, user?.userId]);

  const toggleFavoriteMode = useCallback((mode: WorkspaceMode) => {
    const prev = favoriteModesRef.current;
    const next = prev.includes(mode)
      ? prev.filter((item) => item !== mode)
      : [...prev, mode];

    favoriteModesRef.current = next;
    setFavoriteModes(next);
    writeSidebarFavoriteModes(favoriteStorageScope, next);

    if (!user?.userId) return;

    void updateSidebarFavoriteModes(next).catch(() => {
      favoriteModesRef.current = prev;
      setFavoriteModes(prev);
      writeSidebarFavoriteModes(favoriteStorageScope, prev);
      addToast({
        type: 'error',
        title: t('toast.favoriteSaveFail'),
        description: t('toast.retryLater'),
      });
    });
  }, [addToast, favoriteStorageScope, t, updateSidebarFavoriteModes, user?.userId]);

  const renderModeItem = useCallback((item: SidebarNavItemConfig, options?: { alwaysShowFavorite?: boolean }) => {
    const info = WORKSPACE_MODES[item.mode];
    const isActive = activeMode === item.mode;
    const isFavorite = favoriteModeSet.has(item.mode);
    const ModeIcon = info.icon;
    const useWorkspaceAvatar = isCoreWorkspaceMode(item.mode);
    const profileId = useWorkspaceAvatar ? workspaceModeToAgentProfileId(item.mode) : null;
    const profilePic = profileId ? agentPictures[profileId] : undefined;
    /* Tenant-supplied avatar override for the integrated agent
     * bubble (Phase 12 — tenant assets). Falls through to the
     * legacy letter rendering when the tenant manifest doesn't
     * declare `assets.workspaceModes.agent.avatar`, so B2C and
     * any tenant that opts out preserves byte-perfect parity. */
    const tenantModeAvatar =
      useWorkspaceAvatar && item.mode === 'agent'
        ? resolveWorkspaceModeAvatar(tenant, 'agent')
        : null;
    const customPic = profilePic ?? tenantModeAvatar?.src ?? null;
    const customPicAlt = tenantModeAvatar?.alt ?? item.label;
    const avatarKey = `${item.mode}:${customPic || 'none'}`;
    const showCustomAvatar = useWorkspaceAvatar && !!customPic && !modeAvatarErrorIds[avatarKey];

    return (
      <ModeItem
        key={item.mode}
        data-track={`sidebar_${String(item.mode).replace(/-/g, '_')}`}
        $active={isActive}
        role="button"
        tabIndex={0}
        onClick={() => openModePage(item.mode)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openModePage(item.mode);
          }
        }}
      >
        <ModeAvatarWrap>
          <ModeAvatarCircle $active={isActive}>
            {showCustomAvatar ? (
              <ModeAvatarImg
                src={customPic}
                alt={customPicAlt}
                referrerPolicy="no-referrer"
                onError={() => markModeAvatarError(avatarKey)}
              />
            ) : useWorkspaceAvatar ? (
              info.letter
            ) : (
              <ModeIcon size={14} />
            )}
          </ModeAvatarCircle>
          {useWorkspaceAvatar && <ModeAvatarDot $color={info.color} />}
        </ModeAvatarWrap>
        <ModeItemLabel>{item.label}</ModeItemLabel>
        <ModeItemMeta>
          {item.mode === 'message' && (item.unreadCount ?? 0) > 0 && (
            <SidebarUnreadBadge>
              {(item.unreadCount ?? 0) > 99 ? '99+' : item.unreadCount}
            </SidebarUnreadBadge>
          )}
          <FavoriteBtn
            type="button"
            $favorited={isFavorite}
            $alwaysVisible={options?.alwaysShowFavorite}
            onClick={(e) => {
              e.stopPropagation();
              toggleFavoriteMode(item.mode);
            }}
            aria-label={isFavorite ? `${item.label} ${t('sidebar.removeFavorite')}` : `${item.label} ${t('sidebar.addFavorite')}`}
            title={isFavorite ? t('sidebar.removeFavorite') : t('sidebar.addFavorite')}
          >
            <Star size={14} fill={isFavorite ? 'currentColor' : 'none'} />
          </FavoriteBtn>
        </ModeItemMeta>
      </ModeItem>
    );
  }, [activeMode, agentPictures, favoriteModeSet, modeAvatarErrorIds, openModePage, t, tenant, toggleFavoriteMode]);

  /* ---- 전문 에이전트 항목 렌더 ----
   *
   * WorkspaceMode 와는 다른 경로(`/specialized/<id>`) 로 이동한다.
   * Favorite 별 / unread 뱃지 같은 모드 전용 affordance 는 의도적으로
   * 제외 — 전문 에이전트는 매니페스트가 직접 큐레이션하는 정적
   * 카탈로그이고, 사용자별 즐겨찾기 개념이 아직 없다. ModeItem /
   * ModeAvatarWrap / ModeAvatarCircle 스타일 토큰은 그대로 재사용해
   * 워크스페이스 모드 항목과 시각적 통일감을 유지한다. */
  const renderSpecializedAgentItem = useCallback(
    (agent: TenantSpecializedAgent) => {
      const AgentIcon = resolveSpecializedAgentIcon(agent.iconKey);
      const isActive = location.pathname === agent.path
        || location.pathname.startsWith(`${agent.path}/`);
      const displayLabel = agent.labelKey ? t(agent.labelKey) : agent.label;
      return (
        <ModeItem
          key={agent.id}
          data-track={`sidebar_specialized_${agent.id.replace(/-/g, '_')}`}
          $active={isActive}
          role="button"
          tabIndex={0}
          onClick={() => navigate(agent.path)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              navigate(agent.path);
            }
          }}
        >
          <ModeAvatarWrap>
            <ModeAvatarCircle $active={isActive}>
              <AgentIcon size={14} />
            </ModeAvatarCircle>
          </ModeAvatarWrap>
          <ModeItemLabel>{displayLabel}</ModeItemLabel>
        </ModeItem>
      );
    },
    [location.pathname, navigate, t],
  );

  /* ---- 사용량 계산 (개인 or 팀) ---- */
  const hasMemberTokenQuota = isTeamView
    && typeof selectedTeam?.my_tokens_allocated === 'number'
    && typeof selectedTeam?.my_tokens_used === 'number';
  const displayTokenRemain = isTeamView
    ? (hasMemberTokenQuota
      ? Math.max(0, (selectedTeam!.my_tokens_allocated ?? 0) - (selectedTeam!.my_tokens_used ?? 0))
      : Math.max(0, selectedTeam!.tokens_total - selectedTeam!.tokens_used))
    : Math.max(0, usage.tokensLimit - usage.tokensUsed);
  const displayTokenTotal = isTeamView
    ? (hasMemberTokenQuota ? Math.max(0, selectedTeam!.my_tokens_allocated ?? 0) : selectedTeam!.tokens_total)
    : usage.tokensLimit;
  const tokenRemainPct = displayTokenTotal > 0 ? (displayTokenRemain / displayTokenTotal) * 100 : 0;
  const tokenBarColor =
    tokenRemainPct >= 50
      ? (theme.mode === 'dark' ? '#555' : '#c0c0c0')
      : tokenRemainPct >= 20 ? theme.colors.usageBarCaution
      : theme.colors.usageBarCritical;

  const displayStorageUsed = isTeamView ? selectedTeam!.storage_used_bytes : fileStorageBytes;
  const displayStorageTotal = isTeamView ? selectedTeam!.storage_total_bytes : usage.storageLimitBytes;
  const storageUsedPct = displayStorageTotal > 0 ? (displayStorageUsed / displayStorageTotal) * 100 : 0;
  const storageBarColor =
    storageUsedPct <= 70
      ? (theme.mode === 'dark' ? '#555' : '#c0c0c0')
      : storageUsedPct <= 90
        ? theme.colors.usageBarCaution
        : theme.colors.usageBarCritical;

  const queryClient = useQueryClient();
  const flushWallpaperToCloud = useUIStore((s) => s.flushWallpaperToCloud);
  const flushThemeToCloud = useUIStore((s) => s.flushThemeToCloud);

  const handleLogout = () => {
    // 배경화면/테마 변경 직후 로그아웃하면 debounce 저장이 취소될 수 있으므로
    // 활성 계정이 비워지기 전에 keepalive 요청으로 한 번 더 동기화한다.
    void flushWallpaperToCloud({ keepalive: true }).catch(() => {
      // 배경화면 동기화 실패 시에도 로그아웃은 진행
    });
    void flushThemeToCloud({ keepalive: true }).catch(() => {
      // 테마 동기화 실패 시에도 로그아웃은 진행
    });
    // UX 우선: 즉시 로컬 로그아웃하고, 서버 세션 정리는 백그라운드로 보낸다.
    void logoutCurrentSession({ keepalive: true }).catch(() => {
      // 서버 로그아웃 실패 시에도 로컬 정리는 유지
    });
    logoutActiveAccount();
    resetRuntimeSessionState();
    queryClient.clear();
    clearQuerySessionStorage();
    // App 로컬 인증 상태를 초기화하기 위해 전체 리로드로 이동.
    // Phase 11-3a: `modeToPath('agent')` 로 흡수해 on-prem 테넌트는
    // agent 모드 없을 경우 workspace.defaultPath 로 자동 fallback.
    window.location.href = modeToPath('agent');
  };

  return (
    <>
      <Overlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <Container $open={sidebarOpen}>
        <InnerWrap $open={sidebarOpen} $introActive={playOnboardingIntro}>
          <TopScrollable>
            {/* ---- 상단: 로고 좌측 + 패널 접기 버튼 우측 ----
             *  Resolution policy:
             *    1. `appNameLogo` (wordmark) — when the manifest ships
             *       a wordmark, render it. B2C lands here in light mode;
             *       the resolver returns null in dark mode unless the
             *       tenant ships a `*Dark` variant, in which case the
             *       branch falls through to (2).
             *    2. `agentLogo` (square brand mark) — every manifest
             *       MUST declare `assets.agentLogo`, so this is a safe
             *       universal fallback. Enterprise tenants that ship a
             *       single `logo.svg` (e.g. Samsung's SOLA) land here
             *       and finally render the brand in the sidebar header.
             *  Both branches use the same `openWorkspaceDraft('agent')`
             *  click handler so the header logo always opens the agents
             *  start page (a fresh landing, not a resumed conversation),
             *  regardless of which slot supplied the image. */}
            <Header>
              {appNameLogo ? (
                <AppNameLogo
                  src={appNameLogo.src}
                  alt={appNameLogo.alt ?? 'App Name'}
                  onClick={() => openWorkspaceDraft('agent')}
                />
              ) : (
                <BrandMarkLogo
                  src={tenant.assets.agentLogo}
                  alt={`${tenant.identity.appName} 로고`}
                  onClick={() => openWorkspaceDraft('agent')}
                />
              )}
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                <PanelLeftClose size={18} />
              </Button>
            </Header>

            {/* ---- 개인/팀 워크스페이스 토글 (팀이 있을 때만) ---- */}
            {sidebarTeamSwitcherEnabled && myTeams.length > 0 && (
              <WsToggleWrap>
                <WsToggleBtn
                  $active={wsView === 'personal'}
                  onClick={() => setWsView('personal')}
                >
                  {t('sidebar.personal')}
                </WsToggleBtn>
                <WsToggleBtn
                  $active={wsView !== 'personal'}
                  onClick={handleTeamToggle}
                >
                  {selectedTeam ? selectedTeam.name : t('sidebar.team')}
                </WsToggleBtn>
              </WsToggleWrap>
            )}

            {sidebarFavoritesEnabled && favoriteItems.length > 0 && (
              <>
                <SectionTitleRow onClick={() => toggleSection('favorites')}>
                  {t('sidebar.favorites')}
                  <SectionChevron $open={sectionOpen.favorites}>
                    {sectionOpen.favorites ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </SectionChevron>
                </SectionTitleRow>
                <SectionBody $open={sectionOpen.favorites}>
                  <SectionList>
                    {favoriteItems.map((item) => renderModeItem(item, { alwaysShowFavorite: true }))}
                  </SectionList>
                </SectionBody>
              </>
            )}

            {/* ---- 섹션: Mode ---- */}
            {visibleWorkspaceItems.length > 0 && (
              <>
                <SectionTitleRow onClick={() => toggleSection('workspace')}>
                  {t('sidebar.workspace')}
                  <SectionChevron $open={sectionOpen.workspace}>
                    {sectionOpen.workspace ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </SectionChevron>
                </SectionTitleRow>
                <SectionBody $open={sectionOpen.workspace}>
                  <SectionList>
                    {visibleWorkspaceItems.map((item) => renderModeItem(item))}
                  </SectionList>
                </SectionBody>
              </>
            )}

            {/* ---- 섹션: 전문 에이전트 (매니페스트 specializedAgents 가 비어있지 않을 때만) ---- */}
            {specializedAgents.length > 0 && (
              <>
                <SectionTitleRow onClick={() => toggleSection('specializedAgents')}>
                  {t('sidebar.specializedAgents')}
                  <SectionChevron $open={sectionOpen.specializedAgents}>
                    {sectionOpen.specializedAgents ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </SectionChevron>
                </SectionTitleRow>
                <SectionBody $open={sectionOpen.specializedAgents}>
                  <SectionList>
                    {specializedAgents.map((agent) => renderSpecializedAgentItem(agent))}
                  </SectionList>
                </SectionBody>
              </>
            )}

            {/* ---- 섹션: 팀 워크스페이스 (팀이 있을 때만) ---- */}
            {canManageTeamWorkspace && visibleTeamWorkspaceItems.length > 0 && (
              <>
                <SectionTitleRow onClick={() => toggleSection('teamWorkspace')}>
                  {t('sidebar.teamWorkspace')}
                  <SectionChevron $open={sectionOpen.teamWorkspace}>
                    {sectionOpen.teamWorkspace ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </SectionChevron>
                </SectionTitleRow>
                <SectionBody $open={sectionOpen.teamWorkspace}>
                  <SectionList>
                    {visibleTeamWorkspaceItems.map((item) => renderModeItem(item))}
                  </SectionList>
                </SectionBody>
              </>
            )}

            {/* ---- 섹션: Message ---- */}
            {visiblePersonalItems.length > 0 && (
              <>
                <SectionTitleRow onClick={() => toggleSection('message')}>
                  {t('sidebar.personal')}
                  <SectionChevron $open={sectionOpen.message}>
                    {sectionOpen.message ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </SectionChevron>
                </SectionTitleRow>
                <SectionBody $open={sectionOpen.message}>
                  <SectionList>
                    {visiblePersonalItems.map((item) => renderModeItem(item))}
                  </SectionList>
                </SectionBody>
              </>
            )}

            {/* ---- 섹션: 관리 ---- */}
            {sidebarManagementSectionEnabled && visibleManageItems.length > 0 && (
              <>
                <SectionTitleRow onClick={() => toggleSection('manage')}>
                  {t('sidebar.management')}
                  <SectionChevron $open={sectionOpen.manage}>
                    {sectionOpen.manage ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </SectionChevron>
                </SectionTitleRow>
                <SectionBody $open={sectionOpen.manage}>
                  <SectionList>
                    {visibleManageItems.map((item) => renderModeItem(item))}
                  </SectionList>
                </SectionBody>
              </>
            )}

            {/* ---- 엔터프라이즈 전용: 관리자 콘솔 진입점 ----
             * P11-5j: gated by `sidebar.adminConsoleLink` (off in B2C)
             * AND the underlying admin permission so the link never
             * leaks to non-admins.
             *
             * Re-uses `<ModeItem>` + `<ModeAvatarCircle>` exactly the way
             * `renderModeItem` does for the workspace/manage modes so the
             * entry's avatar slot, label spacing, hover ramp, and active
             * highlight all match the surrounding entries pixel-for-pixel.
             * Without the avatar wrapper the bare 14px `Shield` floats in
             * the row at a different baseline and looks like a different
             * component entirely.  We also reuse the same `SectionList`
             * gutter so the row aligns horizontally with the manage
             * section above. */}
            {sidebarAdminConsoleLinkEnabled && canManageUsers && (() => {
              const adminConsoleActive =
                location.pathname.startsWith('/admin');
              /* `renderModeItem` 의 워크스페이스 모드 항목들은 클릭 시
               * 외부 Sidebar 를 닫지 않습니다(=`setSidebarOpen(false)`
               * 호출 안 함). admin 진입점도 동일하게 outer Sidebar 를
               * 그대로 유지해, 클릭한 뒤에도 사용자가 다른 모드로 한
               * 클릭에 돌아갈 수 있도록 합니다. */
              const openAdminConsole = () => {
                navigate('/admin/users');
              };
              return (
                <SectionList>
                  <ModeItem
                    data-track="sidebar_admin_console"
                    $active={adminConsoleActive}
                    role="button"
                    tabIndex={0}
                    onClick={openAdminConsole}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openAdminConsole();
                      }
                    }}
                  >
                    <ModeAvatarWrap>
                      <ModeAvatarCircle $active={adminConsoleActive}>
                        <Shield size={14} />
                      </ModeAvatarCircle>
                    </ModeAvatarWrap>
                    <ModeItemLabel>{t('sidebar.adminConsole')}</ModeItemLabel>
                  </ModeItem>
                </SectionList>
              );
            })()}
          </TopScrollable>

          {/* ---- 하단 고정: 문의하기 + 사용량 + 프로필 ---- */}
          <BottomFixed>
            {/* ---- 문의하기 ----
             * Hidden when `sidebar.contactLink` is off. The contact tab
             * inside the settings modal is gated separately by
             * `page.settings.contact`, so a tenant can keep the tab
             * reachable from elsewhere while removing this entry. */}
            {sidebarContactLinkEnabled && (
              <ContactBtn
                onClick={() => {
                  emitAppEvent('app:openSettings', { initialTab: 'contact' });
                }}
              >
                <MessageCircleQuestion size={16} />
                {t('sidebar.contact')}
              </ContactBtn>
            )}

            {/* ---- 사용량 바 ---- */}
            {sidebarUsageUpgradeEnabled && (
              <SidebarUsageSection
                isTeamView={isTeamView}
                selectedTeamName={selectedTeam?.name}
                upgradeLabel={
                  user?.plan === 'max'
                    ? t('button.changePlan')
                    : t('button.upgrade')
                }
                onClick={() => {
                  if (!isTeamView) navigate('/pricing');
                }}
                displayTokenRemain={displayTokenRemain}
                displayTokenTotal={displayTokenTotal}
                tokenRemainPct={tokenRemainPct}
                tokenBarColor={tokenBarColor}
                displayStorageUsed={displayStorageUsed}
                displayStorageTotal={displayStorageTotal}
                storageUsedPct={storageUsedPct}
                storageBarColor={storageBarColor}
              />
            )}

            {/* ---- 하단 프로필 ---- */}
            {sidebarProfileMenuEnabled && (
              <Footer>
                <ProfileRow
                  onContextMenu={(e) => {
                    e.preventDefault();
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setMenu({ open: true, x: rect.left, y: rect.top - 4 });
                  }}
                >
                  <AvatarCircle $bg={me?.picture && !imgError ? 'transparent' : profileAvatarBg} $size={32}>
                    {me?.picture && !imgError ? (
                      <AvatarImage
                        src={me.picture}
                        alt="profile"
                        referrerPolicy="no-referrer"
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      profileAvatarText
                    )}
                  </AvatarCircle>
                  <ProfileTextWrap>
                    <ProfileNameRow>
                      <ProfileName>{me?.name || t('sidebar.user')}</ProfileName>
                      {user && <PlanChip>{planLabel(user.plan)}</PlanChip>}
                    </ProfileNameRow>
                    <ProfileEmail>{me?.email || ''}</ProfileEmail>
                  </ProfileTextWrap>
                  <MoreBtn
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      setMenu({ open: true, x: rect.left, y: rect.top - 4 });
                    }}
                  >
                    <EllipsisVertical size={16} />
                  </MoreBtn>
                </ProfileRow>
              </Footer>
            )}
          </BottomFixed>

          <DropdownMenu
            open={menu.open}
            x={menu.x}
            y={menu.y}
            direction="up"
            align="left"
            onClose={() => setMenu((prev) => ({ ...prev, open: false }))}
            /* The "변경하기" entry is dropped when
             * `sidebar.changePlanItem` is off so the menu doesn't
             * dead-link into a `/pricing` page that on-prem tenants
             * usually disable via `page.pricing`. */
            items={[
              {
                id: 'settings',
                label: t('sidebar.settings'),
                icon: <Settings2 size={14} />,
                onClick: () => { emitAppEvent('app:openSettings'); setMenu((prev) => ({ ...prev, open: false })); },
              },
              ...(sidebarChangePlanItemEnabled
                ? [{
                    id: 'plan',
                    label: t('button.changePlan'),
                    icon: <CreditCard size={14} />,
                    onClick: () => { navigate('/pricing'); },
                  }]
                : []),
              {
                id: 'theme',
                label: darkMode ? t('sidebar.lightMode') : t('sidebar.darkMode'),
                icon: darkMode ? <Sun size={14} /> : <Moon size={14} />,
                onClick: toggleDarkMode,
              },
              {
                id: 'logout',
                label: t('sidebar.logout'),
                icon: <LogOut size={14} />,
                danger: true,
                onClick: handleLogout,
              },
            ]}
          />
        </InnerWrap>
      </Container>

      {/* ---- 팀 선택 모달 (2개 이상). teamSwitcher 가 비활성화된
              테넌트는 토글 자체가 사라지므로 모달도 자동으로 닫힌 상태로
              유지된다. */}
      {sidebarTeamSwitcherEnabled && (
        <TeamSelectorModal
          open={teamModalOpen}
          teams={myTeams}
          activeTeamId={wsView}
          onSelect={setWsView}
          onClose={() => setTeamModalOpen(false)}
        />
      )}
    </>
  );
};

